<script lang="ts" setup>
import { computed, reactive } from 'vue'
import { useValidationRules } from '@/composables/useValidationRules'

export interface PersonalInfoData {
  name: string
  surname: string
  org: string
  account_type: 'person' | 'organization'
}

const props = defineProps<{
  modelValue: PersonalInfoData
  disabled: boolean
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: PersonalInfoData): void
}>()

const { name: validateName, required } = useValidationRules()

// Validation state
const errors = reactive({
  name: '' as string | true,
  surname: '' as string | true,
  org: '' as string | true
})

const touched = reactive({
  name: false,
  surname: false,
  org: false
})

const isPerson = computed(() => props.modelValue.account_type === 'person')

// Validate field and return error message or true
const validateField = (field: 'name' | 'surname' | 'org', value: string): string | true => {
  if (field === 'org') {
    return required(value)
  }
  return validateName(value)
}

const handleBlur = (field: 'name' | 'surname' | 'org') => {
  touched[field] = true
  const value = field === 'name' ? props.modelValue.name :
                field === 'surname' ? props.modelValue.surname :
                props.modelValue.org
  errors[field] = validateField(field, value)
}

const updateField = <K extends keyof PersonalInfoData>(field: K, value: PersonalInfoData[K]) => {
  emit('update:modelValue', {
    ...props.modelValue,
    [field]: value
  })

  // Real-time validation after field has been touched
  if (touched[field as 'name' | 'surname' | 'org']) {
    errors[field as 'name' | 'surname' | 'org'] = validateField(field as 'name' | 'surname' | 'org', value as string)
  }
}

// Helper to get error message for display
const getError = (field: 'name' | 'surname' | 'org'): string => {
  const error = errors[field]
  return typeof error === 'string' && error !== '' ? error : ''
}
</script>

<template>
  <div class="form-section">
    <h3 class="form-section-title">
      <v-icon icon="mdi-account" size="18" />
      <span>{{
        isPerson
          ? $t('dashboard.account.stammdaten.name')
          : $t('dashboard.account.stammdaten.organization')
      }}</span>
    </h3>

    <div v-if="isPerson" class="form-row">
      <div class="form-group">
        <label class="form-label">{{ $t('dashboard.account.stammdaten.firstName') }}</label>
        <input
          :value="modelValue.name"
          type="text"
          class="form-input"
          :disabled="disabled"
          :class="{ disabled: disabled, 'has-error': getError('name') }"
          :placeholder="$t('dashboard.account.stammdaten.firstName')"
          @input="updateField('name', ($event.target as HTMLInputElement).value)"
          @blur="handleBlur('name')"
        />
        <Transition name="error-slide">
          <span v-if="getError('name')" class="field-error">{{ getError('name') }}</span>
        </Transition>
      </div>
      <div class="form-group">
        <label class="form-label">{{ $t('dashboard.account.stammdaten.lastName') }}</label>
        <input
          :value="modelValue.surname"
          type="text"
          class="form-input"
          :disabled="disabled"
          :class="{ disabled: disabled, 'has-error': getError('surname') }"
          :placeholder="$t('dashboard.account.stammdaten.lastName')"
          @input="updateField('surname', ($event.target as HTMLInputElement).value)"
          @blur="handleBlur('surname')"
        />
        <Transition name="error-slide">
          <span v-if="getError('surname')" class="field-error">{{ getError('surname') }}</span>
        </Transition>
      </div>
    </div>

    <div v-else class="form-row">
      <div class="form-group full-width">
        <label class="form-label">{{ $t('dashboard.account.stammdaten.organization') }}</label>
        <input
          :value="modelValue.org"
          type="text"
          class="form-input"
          :disabled="disabled"
          :class="{ disabled: disabled, 'has-error': getError('org') }"
          :placeholder="$t('dashboard.account.stammdaten.organizationName')"
          @input="updateField('org', ($event.target as HTMLInputElement).value)"
          @blur="handleBlur('org')"
        />
        <Transition name="error-slide">
          <span v-if="getError('org')" class="field-error">{{ getError('org') }}</span>
        </Transition>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.form-section {
  margin-bottom: 32px;
  padding-bottom: 28px;
  border-bottom: 1px solid rgba(120, 163, 255, 0.08);

  &:last-of-type {
    border-bottom: none;
    margin-bottom: 0;
    padding-bottom: 0;
  }
}

.form-section-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.9rem;
  font-weight: 600;
  color: rgba(200, 218, 255, 0.7);
  margin: 0 0 20px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.form-row {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;

  &.full-width {
    grid-column: 1 / -1;
  }
}

.form-label {
  font-size: 0.8rem;
  font-weight: 500;
  color: rgba(200, 218, 255, 0.6);
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.form-input {
  width: 100%;
  padding: 12px 14px;
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(120, 163, 255, 0.15);
  border-radius: 10px;
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.95);
  transition: all 0.2s ease;

  &::placeholder {
    color: rgba(200, 218, 255, 0.3);
  }

  &:focus {
    outline: none;
    border-color: #00b0ff;
    background: rgba(0, 176, 255, 0.05);
    box-shadow: 0 0 0 3px rgba(0, 176, 255, 0.1);
  }

  &:disabled,
  &.disabled {
    background: rgba(255, 255, 255, 0.02);
    border-color: rgba(120, 163, 255, 0.08);
    color: rgba(255, 255, 255, 0.6);
    cursor: not-allowed;
  }

  &.has-error {
    border-color: #ef5350;
    background: rgba(244, 67, 54, 0.05);

    &:focus {
      border-color: #ef5350;
      box-shadow: 0 0 0 3px rgba(244, 67, 54, 0.1);
    }
  }
}

/* Field Error */
.field-error {
  display: block;
  font-size: 0.75rem;
  color: #ef5350;
  margin-top: 4px;
}

/* Error Transition */
.error-slide-enter-active {
  transition: all 0.2s ease;
}

.error-slide-leave-active {
  transition: all 0.15s ease;
}

.error-slide-enter-from,
.error-slide-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

/* Light Theme */
.v-theme--lightTheme {
  .form-section {
    border-color: rgba(28, 110, 227, 0.08);
  }

  .form-section-title {
    color: rgba(15, 23, 42, 0.6);
  }

  .form-label {
    color: rgba(15, 23, 42, 0.6);
  }

  .form-input {
    background: rgba(0, 0, 0, 0.03);
    border-color: rgba(28, 110, 227, 0.15);
    color: rgba(15, 23, 42, 0.95);

    &::placeholder {
      color: rgba(15, 23, 42, 0.3);
    }

    &:focus {
      border-color: #1c6ee3;
      background: rgba(28, 110, 227, 0.03);
      box-shadow: 0 0 0 3px rgba(28, 110, 227, 0.1);
    }

    &:disabled,
    &.disabled {
      background: rgba(0, 0, 0, 0.02);
      color: rgba(15, 23, 42, 0.5);
    }

    &.has-error {
      border-color: #d32f2f;
      background: rgba(211, 47, 47, 0.03);

      &:focus {
        border-color: #d32f2f;
        box-shadow: 0 0 0 3px rgba(211, 47, 47, 0.1);
      }
    }
  }

  .field-error {
    color: #d32f2f;
  }
}

/* Responsive */
@media (max-width: 768px) {
  .form-row {
    grid-template-columns: 1fr;
  }
}

/* Reduced Motion */
@media (prefers-reduced-motion: reduce) {
  .form-input {
    transition: none;
  }
}
</style>
